import React, { useState, useEffect } from 'react';
import axios from './lib/axiosClient';

const C = {
  bg: '#0d0d14', card: '#13131e', border: '#1e1e2e',
  text: '#f1f1f1', muted: '#888', amber: '#f5a623',
  green: '#10b981', red: '#ef4444',
};

export default function SalesHistory() {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all, cash, card, venmo, zelle
  const [dateFilter, setDateFilter] = useState('month'); // today, week, month, all

  useEffect(function() { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const r = await axios.get('/api/smokeshop-sales');
      setSales(r.data.sales || []);
    } catch(e) { setSales([]); }
    setLoading(false);
  }

  function filtered() {
    const now = new Date();
    return sales.filter(function(s) {
      // Date filter
      const d = new Date(s.created_at);
      if (dateFilter === 'today') {
        if (d.toDateString() !== now.toDateString()) return false;
      } else if (dateFilter === 'week') {
        const w = new Date(now); w.setDate(now.getDate()-7);
        if (d < w) return false;
      } else if (dateFilter === 'month') {
        if (d.getMonth() !== now.getMonth() || d.getFullYear() !== now.getFullYear()) return false;
      }
      // Payment filter
      if (filter !== 'all' && s.payment_method !== filter) return false;
      // Search
      if (search) {
        const q = search.toLowerCase();
        if (!(s.customer_name || '').toLowerCase().includes(q) &&
            !(s.receipt_number || '').toLowerCase().includes(q) &&
            !(s.staff_name || '').toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }

  const rows = filtered();
  const totalRevenue = rows.reduce(function(s,r){ return s + parseFloat(r.total||0); }, 0);
  const cashTotal = rows.filter(function(r){ return r.payment_method==='cash'; }).reduce(function(s,r){ return s+parseFloat(r.total||0); },0);
  const cardTotal = rows.filter(function(r){ return r.payment_method==='card'; }).reduce(function(s,r){ return s+parseFloat(r.total||0); },0);

  const inp = { padding:'8px 12px', background:C.card, border:'1px solid '+C.border, borderRadius:8, color:C.text, fontSize:13, outline:'none' };
  const btn = function(active) { return { padding:'6px 14px', background: active ? C.amber : C.card, color: active ? '#000' : C.muted, border:'1px solid '+(active ? C.amber : C.border), borderRadius:8, fontWeight:600, fontSize:12, cursor:'pointer' }; };

  return (
    <div style={{ background:C.bg, minHeight:'100vh', color:C.text, padding:24, fontFamily:"'DM Sans',-apple-system,sans-serif" }}>
      <div style={{ maxWidth:1100, margin:'0 auto' }}>
        <div style={{ marginBottom:24 }}>
          <div style={{ fontSize:22, fontWeight:800, color:C.text }}>🧾 Sales History</div>
          <div style={{ fontSize:13, color:C.muted, marginTop:2 }}>All transactions — searchable and filterable</div>
        </div>

        {/* Summary cards */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:24 }}>
          {[['Total Revenue', totalRevenue, C.amber],['Cash', cashTotal, C.green],['Card', cardTotal, '#6366f1']].map(function(s){ return (
            <div key={s[0]} style={{ background:C.card, border:'1px solid '+C.border, borderRadius:12, padding:'16px 20px' }}>
              <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', color:C.muted, marginBottom:6 }}>{s[0]}</div>
              <div style={{ fontSize:26, fontWeight:900, color:s[2] }}>${s[1].toLocaleString('en-US',{minimumFractionDigits:2})}</div>
              <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>{rows.length} transactions</div>
            </div>
          ); })}
        </div>

        {/* Filters */}
        <div style={{ display:'flex', gap:8, marginBottom:20, flexWrap:'wrap', alignItems:'center' }}>
          <input value={search} onChange={function(e){ setSearch(e.target.value); }} placeholder="🔍 Search customer, receipt, staff..." style={{ ...inp, minWidth:240 }} />
          <div style={{ display:'flex', gap:6 }}>
            {[['today','Today'],['week','This Week'],['month','This Month'],['all','All Time']].map(function(f){ return (
              <button key={f[0]} onClick={function(){ setDateFilter(f[0]); }} style={btn(dateFilter===f[0])}>{f[1]}</button>
            ); })}
          </div>
          <div style={{ display:'flex', gap:6 }}>
            {[['all','All'],['cash','Cash'],['card','Card'],['venmo','Venmo'],['zelle','Zelle']].map(function(f){ return (
              <button key={f[0]} onClick={function(){ setFilter(f[0]); }} style={btn(filter===f[0])}>{f[1]}</button>
            ); })}
          </div>
        </div>

        {/* Table */}
        <div style={{ background:C.card, border:'1px solid '+C.border, borderRadius:14, overflow:'hidden' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1.5fr 1fr 1fr 1fr 0.8fr', padding:'10px 16px', borderBottom:'1px solid '+C.border, fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em', color:C.muted }}>
            <span>Receipt</span><span>Customer</span><span>Staff</span><span>Payment</span><span>Time</span><span style={{ textAlign:'right' }}>Total</span>
          </div>
          {loading ? (
            <div style={{ padding:40, textAlign:'center', color:C.muted }}>Loading...</div>
          ) : rows.length === 0 ? (
            <div style={{ padding:40, textAlign:'center', color:C.muted }}>No sales found</div>
          ) : rows.map(function(s, i){ return (
            <div key={s.id || i} style={{ display:'grid', gridTemplateColumns:'1fr 1.5fr 1fr 1fr 1fr 0.8fr', padding:'12px 16px', borderBottom:'1px solid '+C.border, alignItems:'center', background: i%2===0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
              <span style={{ fontSize:12, color:C.amber, fontFamily:'monospace' }}>{s.receipt_number || '-'}</span>
              <span style={{ fontSize:13, fontWeight:600, color:C.text }}>{s.customer_name || 'Walk-in'}</span>
              <span style={{ fontSize:12, color:C.muted }}>{s.staff_name || '-'}</span>
              <span style={{ fontSize:12 }}>
                <span style={{ background: s.payment_method==='cash' ? '#10b98120' : s.payment_method==='card' ? '#6366f120' : '#f5a62320', color: s.payment_method==='cash' ? C.green : s.payment_method==='card' ? '#818cf8' : C.amber, borderRadius:6, padding:'2px 8px', fontWeight:700, textTransform:'uppercase', fontSize:11 }}>
                  {s.payment_method || 'cash'}
                </span>
              </span>
              <span style={{ fontSize:12, color:C.muted }}>{new Date(s.created_at).toLocaleString('en-US',{month:'short',day:'numeric',hour:'numeric',minute:'2-digit'})}</span>
              <span style={{ fontSize:14, fontWeight:800, color:C.amber, textAlign:'right' }}>${parseFloat(s.total||0).toFixed(2)}</span>
            </div>
          ); })}
        </div>
      </div>
    </div>
  );
}
